import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { useGetSummary, useGetOfferings } from '../queries/onboardingPolls';
import {
  useUpdateProfileSummary,
  useUpdateOffering,
  useDeleteOffering,
  useAddOffering,
} from '../mutations/onboarding';
import useNotification from './useNotification';
import useMediaQuery from './useMediaQuery';
import { useEffect } from 'react';
import useErrorModalStore from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';

/**
 * Custom hook to manage summary and offerings data and mutations
 * Provides a unified interface for fetching and updating profile data
 * @param {boolean} enabled - Whether to enable data fetching (default: true)
 * @returns {Object} Summary and offerings data, loading states, and mutation handlers
 */
const useSummaryAndOfferings = (enabled = true) => {
  const { accessKey, setBusinessName, setLogo } = useAppStore();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { isMobile, isSmallMobile } = useMediaQuery('(max-width: 768px)');
  const isMobileView = isMobile || isSmallMobile;
  const { openErrorModal } = useErrorModalStore();
  // Fetch profile summary
  const {
    data: profileSummary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useGetSummary(accessKey, enabled);

  // Fetch profile offerings
  const {
    data: profileOfferings,
    isLoading: offeringsLoading,
    error: offeringsError,
    refetch: refetchOfferings,
  } = useGetOfferings(accessKey, enabled);

  // Update profile summary mutation
  const updateSummaryMutation = useUpdateProfileSummary(
    (data) => {
      refetchSummary();
      notification.success('Profile summary updated successfully!');
    },
    (error) => {
      console.error('Error updating profile summary:', error);
      openErrorModal(
        getErrorModalMessage('update_profile_summary_error'),
        'update_profile_summary_error'
      );
    }
  );

  // Update offering mutation
  const updateOfferingMutation = useUpdateOffering(
    (data) => {
      queryClient.invalidateQueries({ queryKey: ['offerings', accessKey] });
      queryClient.invalidateQueries({ queryKey: ['profile-offerings'] });
      notification.success('Offering updated successfully!');
    },
    (error) => {
      console.error('Error updating offering:', error);
      openErrorModal(getErrorModalMessage('update_offering_error'), 'update_offering_error');
    }
  );
  const addOfferingMutation = useAddOffering(
    (data) => {
      queryClient.invalidateQueries({ queryKey: ['offerings', accessKey] });
      queryClient.invalidateQueries({ queryKey: ['profile-offerings'] });
      notification.success('Offering added successfully!');
    },
    (error) => {
      console.error('Error adding offering:', error);
      openErrorModal(getErrorModalMessage('add_offering_error'), 'add_offering_error');
    }
  );

  // Delete offering mutation
  const deleteOfferingMutation = useDeleteOffering(
    (data) => {
      refetchOfferings();
      notification.success('Offering deleted successfully!');
    },
    (error) => {
      console.error('Error deleting offering:', error);
      openErrorModal(getErrorModalMessage('delete_offering_error'), 'delete_offering_error');
    }
  );

  // Handler functions
  const handleSaveSummary = (summaryData) => {
    updateSummaryMutation.mutate(summaryData);
  };

  const handleSaveOffering = (offeringId, offeringData) => {
    updateOfferingMutation.mutate({
      offeringId,
      offeringData,
    });
  };

  const handleAddOffering = (offeringData) => {
    addOfferingMutation.mutate(offeringData);
  };

  const handleDeleteOffering = (offeringId) => {
    deleteOfferingMutation.mutate({ offeringId });
  };

  useEffect(() => {
    if (profileSummary?.business_name && profileSummary?.logo) {
      setBusinessName(profileSummary.business_name || '');
      setLogo(profileSummary.logo || '');
    }
  }, [profileSummary?.business_name, profileSummary?.logo]);

  return {
    // Summary data
    profileSummary,
    summaryLoading,
    summaryError,
    refetchSummary,

    // Offerings data
    profileOfferings,
    offeringsLoading,
    offeringsError,
    refetchOfferings,

    // Mutations
    updateSummaryMutation,
    updateOfferingMutation,
    deleteOfferingMutation,
    addOfferingMutation,

    // Handlers
    handleSaveSummary,
    handleSaveOffering,
    handleDeleteOffering,
    handleAddOffering,

    // Loading states
    isSaving: updateSummaryMutation.isPending || updateOfferingMutation.isPending,
    isDeleting: deleteOfferingMutation.isPending,

    notification,
    isMobileView,
  };
};

export default useSummaryAndOfferings;
